import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { fetchWithCsrf } from '../utils/csrf'

interface User {
  id: number
  name: string
  email: string
}

interface JoinClubProps {
  user: User | null
  onSignInClick: () => void
  onSignUpClick: () => void
}

export default function JoinClub({ user, onSignInClick, onSignUpClick }: JoinClubProps) {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState('')
  const [clubName, setClubName] = useState('')

  useEffect(() => {
    if (user && token) {
      handleJoinClub()
    }
  }, [user, token])

  const handleJoinClub = async () => {
    if (!token) return

    setJoining(true)
    setError('')

    try {
      const response = await fetchWithCsrf(`/api/v1/clubs/join/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      const data = await response.json()

      if (response.ok) {
        // Successfully joined, redirect to the club
        navigate(`/clubs/${data.club.id}`)
      } else {
        setError(data.error || 'Failed to join club')
      }
    } catch (err) {
      setError('Something went wrong. Please try again.')
    } finally {
      setJoining(false)
    }
  }

  // If user is not logged in, show sign in prompt
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mb-6">
            <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Join Book Club</h2>
          <p className="text-gray-600 mb-8">
            Sign in with your account to accept this invitation. If you haven't received your login credentials yet, please contact your club admin.
          </p>
          <div className="space-y-3">
            <button
              onClick={onSignInClick}
              className="w-full px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    )
  }

  // If joining in progress or checking
  if (joining) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mb-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Joining Club...</h2>
          <p className="text-gray-600">
            Please wait while we add you to the club
          </p>
        </div>
      </div>
    )
  }

  // If there's an error
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-100 to-pink-100 rounded-full mb-6">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Unable to Join</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return null
}
